/**
 * /crm/settings/reason-codes — admin CRM reason taxonomy (all categories).
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Save } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useCreateReasonCode,
  usePatchReasonCode,
  useReasonCodesSettings,
} from '@/api/bi/crmSettings';
import type { CrmAvoidableHint, CrmReason, CrmReasonCategory } from '@/api/bi/crm';
import { useAccess } from '@/features/AccessProvider';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';

const CATEGORIES: { value: CrmReasonCategory; label: string }[] = [
  { value: 'sla_breach', label: 'SLA breach' },
  { value: 'customer_delay', label: 'Customer delay' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'refund', label: 'Refund' },
  { value: 'shrinkage', label: 'Shrinkage' },
];

/** Matches backend `TAG_TO_REASON` + default fallback `other`. */
const RESERVED_SLA_CODES = new Set([
  'packing_late',
  'billing_late',
  'picking_late',
  'reached_late',
  'return_late',
  'delivery_late',
  'return_entry_late',
  'return_racking_late',
  'other',
]);

function isReservedSla(row: CrmReason): boolean {
  return row.category === 'sla_breach' && RESERVED_SLA_CODES.has(row.code);
}

type RowDraft = {
  label: string;
  order: string;
  parent_id: string;
  is_active: boolean;
  is_avoidable_hint: CrmAvoidableHint;
};

function draftFromRow(r: CrmReason): RowDraft {
  return {
    label: r.label,
    order: String(r.order),
    parent_id: r.parent_id == null ? '' : String(r.parent_id),
    is_active: r.is_active,
    is_avoidable_hint: (r.is_avoidable_hint || '') as CrmAvoidableHint,
  };
}

function draftsEqual(d: RowDraft, r: CrmReason): boolean {
  return (
    d.label === r.label &&
    d.order === String(r.order) &&
    (d.parent_id === '' ? r.parent_id == null : Number(d.parent_id) === r.parent_id) &&
    d.is_active === r.is_active &&
    (d.is_avoidable_hint || '') === (r.is_avoidable_hint || '')
  );
}

export function CrmReasonCodesPage() {
  const { can } = useAccess();
  const canManage = can('crm.settings.reason_codes', 'manage');
  const [category, setCategory] = useState<CrmReasonCategory>('sla_breach');
  const q = useReasonCodesSettings(category);
  const createMut = useCreateReasonCode();
  const patchMut = usePatchReasonCode();

  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [newRow, setNewRow] = useState({
    code: '',
    label: '',
    parent_id: '',
    order: '0',
    is_avoidable_hint: '' as CrmAvoidableHint,
  });

  useEffect(() => {
    if (!q.data) return;
    const next: Record<number, RowDraft> = {};
    for (const r of q.data.results) {
      next[r.id] = draftFromRow(r);
    }
    setDrafts(next);
  }, [q.data]);

  const rows = q.data?.results ?? [];
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }, [rows]);

  function descendantIds(rootId: number): Set<number> {
    const out = new Set<number>();
    const stack = [rootId];
    while (stack.length) {
      const id = stack.pop()!;
      for (const r of rows) {
        if (r.parent_id === id) {
          if (!out.has(r.id)) {
            out.add(r.id);
            stack.push(r.id);
          }
        }
      }
    }
    return out;
  }

  function parentOptionsForRow(row: CrmReason) {
    const banned = new Set<number>([row.id, ...descendantIds(row.id)]);
    return sortedRows.filter((r) => !banned.has(r.id));
  }

  async function saveRow(row: CrmReason) {
    const d = drafts[row.id] ?? draftFromRow(row);
    const body: {
      label?: string;
      order?: number;
      parent_id?: number | null;
      is_active?: boolean;
      is_avoidable_hint?: CrmAvoidableHint;
    } = {};
    if (d.label !== row.label) body.label = d.label;
    if (d.order !== String(row.order)) body.order = Number(d.order) || 0;
    const pid = d.parent_id === '' ? null : Number(d.parent_id);
    if (pid !== row.parent_id) body.parent_id = pid;
    if (d.is_active !== row.is_active) body.is_active = d.is_active;
    const hint = (d.is_avoidable_hint || '') as CrmAvoidableHint;
    if ((row.is_avoidable_hint || '') !== hint) body.is_avoidable_hint = hint;
    if (Object.keys(body).length === 0) {
      notify.message('No changes for this row.');
      return;
    }
    try {
      await patchMut.mutateAsync({ id: row.id, body });
      notify.success('Reason updated.');
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save reason.'));
    }
  }

  async function createReason() {
    if (!newRow.code.trim() || !newRow.label.trim()) {
      notify.error('Code and label are required.');
      return;
    }
    try {
      await createMut.mutateAsync({
        category,
        code: newRow.code.trim(),
        label: newRow.label.trim(),
        parent_id: newRow.parent_id === '' ? null : Number(newRow.parent_id),
        order: Number(newRow.order) || 0,
        is_avoidable_hint: (newRow.is_avoidable_hint || '') as CrmAvoidableHint,
        is_active: true,
      });
      notify.success('Reason created.');
      setNewRow({ code: '', label: '', parent_id: '', order: '0', is_avoidable_hint: '' });
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not create reason.'));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Reason codes"
        description="Create and edit the CRM reason taxonomy used on tickets (including SLA disposition). Ticket forms only show active codes."
        actions={
          canManage ? (
            <Badge tone="neutral">Manage access</Badge>
          ) : (
            <Badge tone="neutral">View only</Badge>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <Label className="text-xs">Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v as CrmReasonCategory)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-96" />
      ) : q.isError ? (
        <ErrorState
          title="Could not load reason codes"
          description={String((q.error as Error)?.message ?? '')}
        />
      ) : (
        <Card>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-surface text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Label</th>
                    <th className="px-3 py-2 text-left">Parent</th>
                    <th className="px-3 py-2 text-left">Order</th>
                    <th className="px-3 py-2 text-left">Active</th>
                    {category === 'sla_breach' ? (
                      <th className="px-3 py-2 text-left">Avoidable hint</th>
                    ) : null}
                    <th className="px-3 py-2 text-right"> </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => {
                    const d = drafts[row.id] ?? draftFromRow(row);
                    const reserved = isReservedSla(row);
                    const dirty = !draftsEqual(d, row);
                    return (
                      <tr key={row.id} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                        <td className="px-3 py-2">
                          <Input
                            value={d.label}
                            disabled={!canManage}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [row.id]: { ...(prev[row.id] ?? draftFromRow(row)), label: e.target.value },
                              }))
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Select
                            value={d.parent_id === '' ? '__none__' : d.parent_id}
                            disabled={!canManage || reserved}
                            onValueChange={(v) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [row.id]: {
                                  ...(prev[row.id] ?? draftFromRow(row)),
                                  parent_id: v === '__none__' ? '' : v,
                                },
                              }))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">(none)</SelectItem>
                              {parentOptionsForRow(row).map((p) => (
                                <SelectItem key={p.id} value={String(p.id)}>
                                  {p.code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="w-24 px-3 py-2">
                          <Input
                            type="number"
                            min={0}
                            value={d.order}
                            disabled={!canManage}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [row.id]: { ...(prev[row.id] ?? draftFromRow(row)), order: e.target.value },
                              }))
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            checked={d.is_active}
                            disabled={!canManage || reserved}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [row.id]: {
                                  ...(prev[row.id] ?? draftFromRow(row)),
                                  is_active: e.target.checked,
                                },
                              }))
                            }
                          />
                        </td>
                        {category === 'sla_breach' ? (
                          <td className="px-3 py-2">
                            <Select
                              value={(d.is_avoidable_hint || '') === '' ? '__empty__' : d.is_avoidable_hint}
                              disabled={!canManage}
                              onValueChange={(v) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [row.id]: {
                                    ...(prev[row.id] ?? draftFromRow(row)),
                                    is_avoidable_hint: (v === '__empty__' ? '' : v) as CrmAvoidableHint,
                                  },
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__empty__">(none)</SelectItem>
                                <SelectItem value="yes">yes</SelectItem>
                                <SelectItem value="no">no</SelectItem>
                                <SelectItem value="unknown">unknown</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        ) : null}
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={!canManage || !dirty || patchMut.isPending}
                            onClick={() => void saveRow(row)}
                          >
                            {patchMut.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Save className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {canManage ? (
              <div className="border-t border-border p-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <Plus className="h-4 w-4" />
                  Add reason
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <Label className="text-xs">Code (snake_case)</Label>
                    <Input
                      className="mt-1 font-mono text-xs"
                      value={newRow.code}
                      onChange={(e) => setNewRow((n) => ({ ...n, code: e.target.value }))}
                      placeholder="e.g. wrong_item_variant"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Label</Label>
                    <Input
                      className="mt-1"
                      value={newRow.label}
                      onChange={(e) => setNewRow((n) => ({ ...n, label: e.target.value }))}
                      placeholder="Display label"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Order</Label>
                    <Input
                      type="number"
                      min={0}
                      className="mt-1"
                      value={newRow.order}
                      onChange={(e) => setNewRow((n) => ({ ...n, order: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Parent</Label>
                    <Select
                      value={newRow.parent_id === '' ? '__none__' : newRow.parent_id}
                      onValueChange={(v) =>
                        setNewRow((n) => ({ ...n, parent_id: v === '__none__' ? '' : v }))
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="(none)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">(none)</SelectItem>
                        {sortedRows.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {category === 'sla_breach' ? (
                    <div>
                      <Label className="text-xs">Avoidable hint</Label>
                      <Select
                        value={newRow.is_avoidable_hint === '' ? '__empty__' : newRow.is_avoidable_hint}
                        onValueChange={(v) =>
                          setNewRow((n) => ({
                            ...n,
                            is_avoidable_hint: (v === '__empty__' ? '' : v) as CrmAvoidableHint,
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="(none)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__empty__">(none)</SelectItem>
                          <SelectItem value="yes">yes</SelectItem>
                          <SelectItem value="no">no</SelectItem>
                          <SelectItem value="unknown">unknown</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                </div>
                <Button className="mt-3" onClick={() => void createReason()} disabled={createMut.isPending}>
                  {createMut.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-4 w-4" />
                  )}
                  Create
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>
      )}
    </>
  );
}
